/**
 * ThemeManager
 *
 * Singleton that manages theme state for PixiJS renderers.
 * Provides colors and notifies renderers when theme changes.
 */

import { DARK_PIXI, LIGHT_PIXI, type PixiThemeColors } from '../config/themes';

type ThemeChangeCallback = (colors: PixiThemeColors) => void;

class ThemeManagerClass {
  private currentMode: 'light' | 'dark' = 'dark';
  private listeners: Set<ThemeChangeCallback> = new Set();

  /**
   * Get current theme colors for PixiJS
   */
  get colors(): PixiThemeColors {
    return this.currentMode === 'light' ? LIGHT_PIXI : DARK_PIXI;
  }

  /**
   * Get current theme mode
   */
  get mode(): 'light' | 'dark' {
    return this.currentMode;
  }

  /**
   * Set theme mode and notify listeners
   */
  setTheme(mode: 'light' | 'dark'): void {
    if (this.currentMode === mode) return;

    this.currentMode = mode;
    // console.log(`🎨 Theme changed to: ${mode}`);

    // Notify all listeners
    this.listeners.forEach(callback => {
      try {
        callback(this.colors);
      } catch (error) {
        console.error('Error in theme change listener:', error);
      }
    });
  }

  /**
   * Subscribe to theme changes
   * Returns unsubscribe function
   */
  subscribe(callback: ThemeChangeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Get a specific color value
   */
  getColor(name: keyof PixiThemeColors): number {
    return this.colors[name];
  }
}

// Export singleton instance
export const ThemeManager = new ThemeManagerClass();
